// Stage-driving bots (tests, benchmark autopilot). They drive the real physics through
// ControlInput: a safe bot, a keyboard bot (digital keys through the player's smoothing) and a
// limit bot at ~90 % of the calibrated limit speed.
import type { Track } from '../world/types';
import type { TrackIndex } from '../world/track';
import type { Vehicle } from '../sim/vehicle';
import type { ControlInput } from '../sim/types';
import { SURFACE_IDS } from '../sim/surfaces';
import { DEG, clamp } from '../sim/math';
import { InputSmoother, emptyRaw } from '../input/smoothing';
import { SpeedController } from './control';

export type BotKind = 'safe' | 'keyboard' | 'limit';

/** Limit lateral acceleration (g) by surface class, from the calibration (see codriver/calibration). */
export interface GripModel {
  latG(surface: string, radius: number): number;
}

export const DEFAULT_GRIP: GripModel = {
  latG: (surface) => (surface === 'tarmac' || surface === 'concrete' ? 0.95 : surface === 'mud' || surface === 'water' ? 0.45 : surface === 'wet' ? 0.58 : 0.66),
};

export interface ProfileOptions {
  /** Fraction of the limit lateral acceleration to use. */
  latFraction: number;
  maxSpeed: number;
  brakeG: number;
  accelG: number;
  /** Max vertical curvature × v² (g) over crests (keeps the safe bot on the ground). */
  crestG: number;
}

/** Speed profile along the track from horizontal curvature, surfaces and vertical crests. */
export function speedProfile(track: Track, grip: GripModel, o: ProfileOptions): Float64Array {
  const n = track.s.length;
  const v = new Float64Array(n);
  const ds = track.step;
  // Curvature envelope over ±8 m so the bot sees the tightest part of each corner early.
  for (let i = 0; i < n; i++) {
    let k = 0;
    for (let j = Math.max(0, i - 8); j <= Math.min(n - 1, i + 8); j++) k = Math.max(k, Math.abs(track.curvature[j]));
    const surf = SURFACE_IDS[track.surface[i]];
    const R = k > 1e-5 ? 1 / k : 1e5;
    const muEff = grip.latG(surf, R) * o.latFraction;
    // Vertical curvature over the next/previous few metres (crest < 0).
    let ycMin = 0;
    for (let j = Math.max(3, i - 6); j <= Math.min(n - 4, i + 6); j++) ycMin = Math.min(ycMin, (track.y[j + 3] - 2 * track.y[j] + track.y[j - 3]) / (9 * ds * ds));
    // Lateral limit with crest unloading: v²/R ≤ µ (g − v² κv)  ⇒  v² ≤ µ g / (1/R + µ κv).
    let vm = Math.min(o.maxSpeed, Math.sqrt((muEff * 9.81) / (1 / R + muEff * Math.max(0, -ycMin))));
    // Narrow sections (bridge) — cap speed.
    if (track.width[i] < 5) vm = Math.min(vm, 70 / 3.6);
    // Crests: v² · κ_vertical ≤ crestG · g (much stricter when the crest is also a corner —
    // landing while turning is how cars roll).
    if (i > 2 && i < n - 3) {
      const yc = (track.y[i + 3] - 2 * track.y[i] + track.y[i - 3]) / (9 * ds * ds);
      const turning = k > 1 / 400;
      if (yc < -1e-4) vm = Math.min(vm, Math.sqrt(((turning ? Math.min(o.crestG, 0.7) : o.crestG) * 9.81) / -yc));
    }
    v[i] = vm;
  }
  // Backward pass (braking) and forward pass (acceleration).
  for (let i = n - 2; i >= 0; i--) v[i] = Math.min(v[i], Math.sqrt(v[i + 1] * v[i + 1] + 2 * o.brakeG * 9.81 * ds));
  for (let i = 1; i < n; i++) v[i] = Math.min(v[i], Math.sqrt(v[i - 1] * v[i - 1] + 2 * o.accelG * 9.81 * ds));
  return v;
}

export const BOT_PROFILES: Record<BotKind, ProfileOptions> = {
  safe: { latFraction: 0.55, maxSpeed: 105 / 3.6, brakeG: 0.35, accelG: 0.35, crestG: 0.55 },
  keyboard: { latFraction: 0.55, maxSpeed: 105 / 3.6, brakeG: 0.32, accelG: 0.35, crestG: 0.55 },
  limit: { latFraction: 0.9, maxSpeed: 200 / 3.6, brakeG: 0.6, accelG: 0.6, crestG: 1.3 },
};

/**
 * Follows the centreline with pure pursuit and tracks the speed profile.
 */
export class StageDriver {
  readonly kind: BotKind;
  readonly profile: Float64Array;
  private readonly track: Track;
  private readonly index: TrackIndex;
  private readonly sc = new SpeedController();
  /** Reused projection result (no object per frame). */
  private readonly proj = { s: 0, d: 0, i: 0 };
  private readonly smoother = new InputSmoother();
  private readonly raw = emptyRaw();
  private readonly out: ControlInput = { steer: 0, throttle: 0, brake: 0, handbrake: 0, shiftUp: false, shiftDown: false };
  private readonly brakeG: number;
  private latInteg = 0;
  hint = -1;
  s = 0;
  lateral = 0;
  /** Seconds to hold the brakes at the start (countdown). */
  constructor(track: Track, index: TrackIndex, kind: BotKind, grip: GripModel = DEFAULT_GRIP) {
    this.kind = kind;
    this.track = track;
    this.index = index;
    this.profile = speedProfile(track, grip, BOT_PROFILES[kind]);
    this.brakeG = BOT_PROFILES[kind].brakeG;
    this.sc.brakeThreshold = 0.5;
    if (kind === 'limit') {
      this.sc.kp = 1.2;
      this.sc.brakeGain = 0.6;
    }
  }

  targetSpeed(s: number): number {
    const i = clamp(Math.round(s / this.track.step), 0, this.profile.length - 1);
    return this.profile[i];
  }

  update(v: Vehicle, dt: number): ControlInput {
    v.readPose();
    const t = this.track;
    const p = this.index.project(v.pos.x, v.pos.z, this.hint, this.proj);
    this.hint = p.i;
    this.s = p.s;
    this.lateral = p.d;
    const speed = v.telemetry.speed * Math.sign(v.telemetry.forwardSpeed || 1);
    // Pure pursuit to a lookahead point on the centreline.
    const L = clamp(0.55 * Math.abs(speed), 6, 26);
    const j = clamp(Math.round((p.s + L) / t.step), 0, t.s.length - 1);
    const dx = t.x[j] - v.pos.x;
    const dz = t.z[j] - v.pos.z;
    const fwd = dx * v.fwd.x + dz * v.fwd.z;
    const left = dx * v.leftDir.x + dz * v.leftDir.z;
    const alpha = Math.atan2(left, Math.max(0.5, fwd));
    const wb = Math.abs(v.spec.wheels[0].position[2] - v.spec.wheels[2].position[2]);
    const Ld = Math.max(1, Math.hypot(dx, dz));
    let delta = Math.atan2(2 * wb * Math.sin(alpha), Ld);
    // Yaw-rate tracking and slip feedback: counter-steer when the car rotates faster than the path
    // asks for or the rear steps out (a pure pursuit alone steers further into a slide).
    const rPath = (speed * (2 * Math.sin(alpha))) / Ld;
    delta -= 0.18 * (v.telemetry.yawRate - rPath);
    const beta = v.telemetry.bodySlipAngle;
    if (Math.abs(beta) > 0.06) delta += 0.6 * (beta - Math.sign(beta) * 0.06);
    // Lateral-error PI: pure pursuit alone under-steers by the tyres' slip angles at speed.
    this.latInteg = clamp(this.latInteg - p.d * dt, -4, 4);
    delta += -0.03 * p.d + 0.012 * this.latInteg;
    const steer = clamp(-delta / (v.spec.steering.maxAngleDeg * DEG), -1, 1);
    // Speed: look slightly ahead (reaction distance).
    const vt = Math.min(this.targetSpeed(p.s + Math.max(2, speed * 0.25)), this.targetSpeed(p.s));
    const c = this.sc.update(speed, vt, dt);
    // Ease off when sliding: power in a big slide pushes the car wide.
    if (Math.abs(v.telemetry.bodySlipAngle) > 0.14) c.throttle *= 0.35;
    // Anticipatory braking: deceleration needed to meet the profile over the next 120 m.
    let need = 0;
    const prof = this.profile;
    const last = prof.length - 1;
    const step = this.track.step;
    for (let d = 4; d <= 120; d += 4) {
      const vd = prof[clamp(Math.round((p.s + d) / step), 0, last)];
      if (speed > vd) need = Math.max(need, (speed * speed - vd * vd) / (2 * d));
    }
    const g = 9.81 * this.brakeG;
    if (need > 0.55 * g) {
      c.throttle = 0;
      c.brake = Math.max(c.brake, clamp((need - 0.35 * g) / (0.75 * g), 0.15, 1));
    }

    if (this.kind === 'keyboard') {
      // Digital keys: steer toward the desired value with a dead-band, on/off pedals.
      const r = this.raw;
      r.digital = true;
      const cur = this.smoother.out.steer;
      r.steer = steer > cur + 0.04 ? 1 : steer < cur - 0.04 ? -1 : 0;
      // If the desired steering is small and we're near it, release.
      if (Math.abs(steer) < 0.02 && Math.abs(cur) < 0.06) r.steer = 0;
      r.throttle = speed < vt - 0.4 ? 1 : 0;
      r.brake = speed > vt + 1.6 ? 1 : 0;
      r.handbrake = 0;
      r.shiftUp = false;
      r.shiftDown = false;
      return this.smoother.update(r, { speed, slipAngle: v.telemetry.bodySlipAngle }, dt);
    }
    const o = this.out;
    o.steer = steer;
    o.throttle = c.throttle;
    o.brake = c.brake;
    o.handbrake = 0;
    return o;
  }
}
